// Round-trip rich composer HTML through the AI without losing images or links.
//
// The AI functions work on plain text. Sending `htmlToPlain(body)` used to strip
// every inline <img> and the href of every <a>, so Polish silently removed
// pasted screenshots and links. Instead we swap each one for a short token the
// model is told to copy through unchanged:
//
//   <img src="blob:...">                 ->  [[img1]]
//   <a href="https://x">click here</a>   ->  [[link1]]click here[[/link1]]
//
// and put the original markup back afterwards. Pure string work (no DOM), so it
// is unit-testable in node.
//
// Safety: the model's text is HTML-escaped before tokens are expanded, so the
// model can never inject markup. Only markup that was already in the editor is
// ever re-inserted.

import { plainToHtmlParagraphs } from './template-render';

export interface AiTokenMap {
  /** `img1` -> the full original `<img ...>` tag. */
  images: Record<string, string>;
  /** `link1` -> original opening `<a ...>` tag + its visible text. */
  links: Record<string, { open: string; text: string }>;
}

export interface TokenizedForAi {
  /** Plain text carrying `[[imgN]]` / `[[linkN]]...[[/linkN]]` tokens. */
  text: string;
  map: AiTokenMap;
}

const IMG_RE = /<img\b[^>]*>/gi;
const LINK_RE = /(<a\b[^>]*\bhref\s*=\s*(?:"[^"]*"|'[^']*')[^>]*>)([\s\S]*?)<\/a>/gi;

function decodeEntities(s: string): string {
  return s
    .replace(/&nbsp;/g, ' ')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&amp;/g, '&');
}

/** Tag-strip rich HTML to plain text, keeping line / paragraph breaks. */
export function htmlToAiPlain(html: string): string {
  if (!html) return '';
  return decodeEntities(
    html
      .replace(/<\s*br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
      .replace(/<[^>]+>/g, ''),
  )
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

export function tokenizeForAi(html: string): TokenizedForAi {
  const map: AiTokenMap = { images: {}, links: {} };
  if (!html) return { text: '', map };

  let imgN = 0;
  // Images first, so an image inside a link becomes a token inside the pair.
  let work = html.replace(IMG_RE, (tag) => {
    imgN += 1;
    const id = `img${imgN}`;
    map.images[id] = tag;
    return `[[${id}]]`;
  });

  let linkN = 0;
  work = work.replace(LINK_RE, (_whole, open: string, inner: string) => {
    linkN += 1;
    const id = `link${linkN}`;
    map.links[id] = { open, text: htmlToAiPlain(inner).replace(/\[\[\/?\w+\]\]/g, '') };
    return `[[${id}]]${inner}[[/${id}]]`;
  });

  return { text: htmlToAiPlain(work), map };
}

export interface RehydratedAi {
  html: string;
  /** Link texts the model dropped entirely; they were re-appended at the end. */
  restoredLinks: string[];
  /** How many images the model dropped; they were re-appended at the end. */
  restoredImages: number;
}

/** Turn the model's text (with tokens) back into editor HTML. */
export function rehydrateFromAi(text: string, map: AiTokenMap): RehydratedAi {
  const usedLinks = new Set<string>();
  const usedImages = new Set<string>();

  // 1. Escape + paragraph-wrap. Tokens contain no characters that get escaped.
  let html = plainToHtmlParagraphs((text ?? '').trim());

  // 2. Link pairs. Keeps the original href even when the model reworded the text.
  html = html.replace(
    /\[\[(link\d+)\]\]([\s\S]*?)\[\[\/\1\]\]/g,
    (_whole, id: string, inner: string) => {
      const link = map.links[id];
      if (!link || usedLinks.has(id)) return inner; // unknown or duplicated token
      usedLinks.add(id);
      return `${link.open}${inner}</a>`;
    },
  );

  // 3. Images.
  html = html.replace(/\[\[(img\d+)\]\]/g, (_w, id: string) => {
    const tag = map.images[id];
    if (!tag || usedImages.has(id)) return '';
    usedImages.add(id);
    return tag;
  });

  // 4. Strip any stray / invented tokens (lone open or close markers).
  html = html.replace(/\[\[\/?(?:link|img)\d+\]\]/g, '');

  // 5. Never silently lose a link or image the writer had.
  const restoredLinks: string[] = [];
  let tail = '';
  for (const [id, link] of Object.entries(map.links)) {
    if (usedLinks.has(id)) continue;
    restoredLinks.push(link.text || id);
    tail += `<p>${link.open}${escapeText(link.text || 'link')}</a></p>`;
  }
  let restoredImages = 0;
  for (const [id, tag] of Object.entries(map.images)) {
    if (usedImages.has(id)) continue;
    restoredImages += 1;
    tail += `<p>${tag}</p>`;
  }

  return { html: html + tail, restoredLinks, restoredImages };
}

function escapeText(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
